import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, 
  Zap, 
  Activity, 
  AlertTriangle,
  Building2,
  Wifi,
  WifiOff,
  RefreshCw,
  Wrench,
  Clock,
  Server,
  Shield,
  DollarSign,
  BarChart3,
  CheckCircle,
  Leaf,
  TreePine,
  TrendingDown,
  Sun,
  Moon
} from 'lucide-react';
import { 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell
} from 'recharts';
import { NativeButton } from '../components/NativeComponents';
import { toast } from 'sonner';
import { getErrorMessage } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const kokpitService = {
  getOverview: async () => {
    const token = sessionStorage.getItem('hims_token') || localStorage.getItem('hims_token');
    const response = await fetch(`${API_URL}/api/kokpit/overview`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('API error');
    return response.json();
  }
};

// Gauge component for cockpit style
const GaugeIndicator = ({ value, max, label, color, icon: Icon }) => {
  const percentage = Math.min((value / max) * 100, 100);
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-20 h-20">
        <svg className="w-full h-full transform -rotate-90">
          <circle cx="40" cy="40" r="35" fill="none" stroke="#1e293b" strokeWidth="6" />
          <circle
            cx="40" cy="40" r="35" fill="none" stroke={color} strokeWidth="6"
            strokeDasharray={`${percentage * 2.2} 220`} strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
      <span className="text-2xl font-bold mt-2" style={{ color }}>{value}</span>
      <span className="text-xs text-slate-400">{label}</span>
    </div>
  );
};

// CO2 Counter with animation
const CO2Counter = ({ value, treesEquivalent }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.round(current * 10) / 10);
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [value]);
  
  return (
    <div className="bg-gradient-to-br from-emerald-900/40 to-green-900/40 rounded-2xl p-6 border border-emerald-500/30">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <Leaf className="w-6 h-6 text-emerald-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-emerald-400">CO₂ Tasarrufu</h3>
          <p className="text-xs text-slate-400">Bu ay elektrikli arac kullanimiyla</p>
        </div>
      </div>
      
      <div className="text-center py-4">
        <div className="text-5xl font-bold text-emerald-400 mb-2">
          {displayValue.toFixed(1)}
          <span className="text-2xl ml-1">kg</span>
        </div>
        <p className="text-slate-400 text-sm">CO₂ emisyonu onlendi</p>
      </div>
      
      <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-emerald-500/20">
        <TreePine className="w-5 h-5 text-emerald-500" />
        <span className="text-emerald-300">
          ≈ <strong>{treesEquivalent}</strong> agacin yillik emilimi
        </span>
      </div>
    </div>
  );
};

// Savings Potential Card
const SavingsPotentialCard = ({ peakCost, savings, explanation }) => {
  return (
    <div className="bg-gradient-to-br from-amber-900/40 to-orange-900/40 rounded-2xl p-6 border border-amber-500/30">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
          <TrendingDown className="w-6 h-6 text-amber-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-amber-400">Tasarruf Potansiyeli</h3>
          <p className="text-xs text-slate-400">Akilli sarj optimizasyonu</p>
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-slate-400">Pik saat maliyeti</span>
          <span className="text-white font-semibold">{peakCost.toLocaleString('tr-TR')} TL</span>
        </div>
        
        <div className="bg-amber-500/20 rounded-xl p-4 text-center">
          <p className="text-xs text-amber-300 mb-1">Aylik tasarruf potansiyeli</p>
          <p className="text-3xl font-bold text-amber-400">{savings.toLocaleString('tr-TR')} TL</p>
        </div>
        
        <p className="text-xs text-slate-400 text-center">{explanation}</p>
      </div>
    </div>
  );
};

// Status dot
const StatusDot = ({ status }) => {
  const colors = {
    healthy: 'bg-emerald-500 shadow-emerald-500/50',
    warning: 'bg-amber-500 shadow-amber-500/50',
    critical: 'bg-red-500 shadow-red-500/50 animate-pulse'
  };
  return <span className={`w-3 h-3 rounded-full ${colors[status] || colors.healthy} shadow-lg`}></span>;
};

export default function KokpitPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const result = await kokpitService.getOverview();
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const formatCurrency = (val) => (val || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const formatTime = (isoStr) => {
    if (!isoStr) return '-';
    const date = new Date(isoStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60);
    if (diff < 1) return 'Az once';
    if (diff < 60) return `${diff} dk once`;
    if (diff < 1440) return `${Math.floor(diff / 60)} saat once`;
    return date.toLocaleDateString('tr-TR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-[#0a0f1e]">
        <RefreshCw className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  const summary = data?.summary || {};
  const revenue = data?.revenue || {};
  const energyAnalysis = data?.energy_analysis || {};
  const co2Data = data?.co2 || {};
  const hourlyData = energyAnalysis.hourly_consumption || [];

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-white p-4 md:p-6" data-testid="kokpit-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            Patron Paneli
          </h1>
          <p className="text-slate-400 text-sm">Sistem Kontrol Merkezi</p>
        </div>
        <NativeButton 
          variant="outline" 
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="border-slate-600 text-slate-300 hover:bg-slate-800"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Yenile
        </NativeButton>
      </div>

      {/* Top Status Bar - Gauge Indicators */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 mb-6 border border-slate-700">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 justify-items-center">
          <GaugeIndicator value={summary.total_active_licenses || 0} max={Math.max(50, summary.total_active_licenses || 1)} label="Aktif Lisans" color="#22d3ee" icon={Shield} />
          <GaugeIndicator value={summary.total_completed_work_orders || 0} max={Math.max(100, summary.total_completed_work_orders || 1)} label="Tamamlanan Is" color="#34d399" icon={CheckCircle} />
          <GaugeIndicator value={summary.total_faults || 0} max={Math.max(20, summary.total_faults || 1)} label="Acik Ariza" color={summary.total_faults > 0 ? "#f87171" : "#94a3b8"} icon={AlertTriangle} />
          <GaugeIndicator value={summary.online_stations || 0} max={Math.max(summary.total_stations || 1, 1)} label={`Online / ${summary.total_stations || 0}`} color="#a78bfa" icon={Server} />
          <GaugeIndicator value={summary.system_uptime?.toFixed(1) || 99.9} max={100} label="Uptime %" color="#fbbf24" icon={Activity} />
        </div>
      </div>

      {/* Energy Analysis Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Hourly Energy Chart */}
        <div className="lg:col-span-2 bg-slate-900/80 rounded-xl p-5 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-cyan-400 flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Saatlik Enerji Tuketimi (Son 24 Saat)
            </h3>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1">
                <Sun className="w-4 h-4 text-amber-400" />
                <span className="text-slate-400">Pik saat</span>
              </div>
              <div className="flex items-center gap-1">
                <Moon className="w-4 h-4 text-blue-400" />
                <span className="text-slate-400">Dusuk tarife</span>
              </div>
            </div>
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="hour" stroke="#64748b" fontSize={10} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={10} tickLine={false} unit=" kWh" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
                  formatter={(value, name) => [
                    name === 'energy_kwh' ? `${value} kWh` : `${value} TL`,
                    name === 'energy_kwh' ? 'Enerji' : 'Maliyet'
                  ]}
                />
                <Bar dataKey="energy_kwh" name="energy_kwh" radius={[4, 4, 0, 0]}>
                  {hourlyData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.is_peak ? '#f59e0b' : '#3b82f6'} 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          <div className="mt-4 flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-amber-500"></div>
              <span className="text-slate-400">Pik saatler (yuksek maliyet)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-blue-500"></div>
              <span className="text-slate-400">Normal saatler</span>
            </div>
          </div>
        </div>

        {/* CO2 Counter */}
        <CO2Counter 
          value={co2Data.co2_saved_kg || 0} 
          treesEquivalent={co2Data.trees_equivalent || 0} 
        />
      </div>

      {/* Savings and Revenue */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Savings Potential */}
        <SavingsPotentialCard 
          peakCost={energyAnalysis.peak_hour_cost || 0}
          savings={energyAnalysis.potential_savings || 0}
          explanation={energyAnalysis.savings_explanation || ""}
        />

        {/* Revenue Panel */}
        <div className="lg:col-span-2 bg-slate-900/80 rounded-xl p-5 border border-slate-700">
          <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Gelir Ozeti
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {/* Lisans Gelirleri */}
            <div className="bg-slate-800/50 rounded-xl p-4">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Lisans Gelirleri</p>
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-slate-400 text-sm">Bugun</span><span className="text-cyan-400 font-bold">{formatCurrency(revenue.today_license)} TL</span></div>
                <div className="flex justify-between"><span className="text-slate-400 text-sm">Bu Ay</span><span className="text-cyan-400 font-bold">{formatCurrency(revenue.month_license)} TL</span></div>
                <div className="flex justify-between"><span className="text-slate-400 text-sm">Toplam</span><span className="text-cyan-400 font-bold">{formatCurrency(revenue.total_license)} TL</span></div>
              </div>
            </div>
            
            {/* İş Emri Gelirleri */}
            <div className="bg-slate-800/50 rounded-xl p-4">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Is Emri Gelirleri</p>
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-slate-400 text-sm">Bugun</span><span className="text-emerald-400 font-bold">{formatCurrency(revenue.today_work_order)} TL</span></div>
                <div className="flex justify-between"><span className="text-slate-400 text-sm">Bu Ay</span><span className="text-emerald-400 font-bold">{formatCurrency(revenue.month_work_order)} TL</span></div>
                <div className="flex justify-between"><span className="text-slate-400 text-sm">Toplam</span><span className="text-emerald-400 font-bold">{formatCurrency(revenue.total_work_order)} TL</span></div>
              </div>
            </div>
            
            {/* Toplam */}
            <div className="bg-gradient-to-br from-cyan-900/30 to-emerald-900/30 rounded-xl p-4 flex flex-col justify-center">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 text-center">Toplam Gelir</p>
              <p className="text-2xl font-bold text-center bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                {formatCurrency(revenue.all_time_total)} TL
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Site Status Grid and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Site Grid */}
        <div className="lg:col-span-8">
          <div className="bg-slate-900/80 rounded-xl p-5 border border-slate-700 h-full">
            <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Site Durumlari ({data?.sites?.length || 0})
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto pr-2">
              {data?.sites?.map((site) => (
                <div 
                  key={site.id} 
                  className={`p-4 rounded-xl border transition-all hover:scale-[1.02] ${
                    site.status === 'critical' ? 'border-red-500/50 bg-red-900/20' :
                    site.status === 'warning' ? 'border-amber-500/50 bg-amber-900/20' :
                    'border-slate-600 bg-slate-800/50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <StatusDot status={site.status} />
                      <span className="font-medium text-white truncate max-w-[120px]">{site.name}</span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      site.license_status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/50 text-slate-400'
                    }`}>
                      {site.license_status === 'active' ? 'Lisansli' : 'Lisanssiz'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-1.5">
                      {site.online_stations > 0 ? <Wifi className="w-3.5 h-3.5 text-emerald-400" /> : <WifiOff className="w-3.5 h-3.5 text-red-400" />}
                      <span className="text-slate-300">{site.online_stations}/{site.total_stations}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <AlertTriangle className={`w-3.5 h-3.5 ${site.open_faults > 0 ? 'text-red-400' : 'text-slate-500'}`} />
                      <span className={site.open_faults > 0 ? 'text-red-400' : 'text-slate-400'}>{site.open_faults} ariza</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Wrench className={`w-3.5 h-3.5 ${site.pending_work_orders > 0 ? 'text-amber-400' : 'text-slate-500'}`} />
                      <span className="text-slate-300">{site.pending_work_orders} is</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-slate-500" />
                      <span className="text-slate-400 text-xs">{formatTime(site.last_activity)}</span>
                    </div>
                  </div>
                </div>
              ))}
              
              {(!data?.sites || data.sites.length === 0) && (
                <div className="col-span-full text-center py-8 text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Henuz site bulunmuyor</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Activities */}
        <div className="lg:col-span-4 space-y-4">
          {/* Son Aktiviteler */}
          <div className="bg-slate-900/80 rounded-xl p-5 border border-slate-700">
            <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Son Aktiviteler
            </h3>
            <div className="space-y-3 max-h-[180px] overflow-y-auto">
              {data?.recent_activities?.map((act, idx) => (
                <div key={idx} className="flex items-start gap-3 text-sm">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    act.type === 'fault' ? 'bg-red-500/20' : 'bg-cyan-500/20'
                  }`}>
                    {act.type === 'fault' ? <AlertTriangle className="w-4 h-4 text-red-400" /> : <Zap className="w-4 h-4 text-cyan-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-300 truncate">{act.description}</p>
                    <p className="text-xs text-slate-500">{formatTime(act.time)}</p>
                  </div>
                </div>
              ))}
              {(!data?.recent_activities || data.recent_activities.length === 0) && (
                <p className="text-slate-500 text-center py-4">Aktivite yok</p>
              )}
            </div>
          </div>

          {/* Açık Arızalar */}
          <div className="bg-slate-900/80 rounded-xl p-5 border border-slate-700">
            <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Acik Arizalar ({data?.open_faults?.length || 0})
            </h3>
            <div className="space-y-2 max-h-[150px] overflow-y-auto">
              {data?.open_faults?.slice(0, 5).map((fault, idx) => (
                <div key={fault.id || idx} className="flex items-center justify-between p-2 bg-red-900/20 rounded-lg border border-red-500/30">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-200 truncate">{fault.title}</p>
                    <p className="text-xs text-slate-500">{fault.site_name}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    fault.priority === 'high' ? 'bg-red-500/30 text-red-300' :
                    fault.priority === 'medium' ? 'bg-amber-500/30 text-amber-300' :
                    'bg-slate-600/50 text-slate-400'
                  }`}>
                    {fault.priority === 'high' ? 'Yuksek' : fault.priority === 'medium' ? 'Orta' : 'Dusuk'}
                  </span>
                </div>
              ))}
              {(!data?.open_faults || data.open_faults.length === 0) && (
                <div className="text-center py-4">
                  <CheckCircle className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                  <p className="text-emerald-400 text-sm">Acik ariza yok</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom - Trend Chart */}
      <div className="mt-6 bg-slate-900/80 rounded-xl p-5 border border-slate-700">
        <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Son 30 Gun Gelir Trendi
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data?.trend || []}>
              <defs>
                <linearGradient id="colorLicense" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#22d3ee" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorWorkOrder" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34d399" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#34d399" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} tickFormatter={(value) => `${value}`} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
                formatter={(value, name) => [
                  `${value?.toLocaleString('tr-TR')} TL`,
                  name === 'license_revenue' ? 'Lisans' : name === 'work_order_revenue' ? 'Is Emri' : 'Toplam'
                ]}
              />
              <Legend 
                formatter={(value) => value === 'license_revenue' ? 'Lisans Geliri' : value === 'work_order_revenue' ? 'Is Emri Geliri' : 'Toplam'}
                wrapperStyle={{ paddingTop: '10px' }}
              />
              <Area type="monotone" dataKey="license_revenue" stroke="#22d3ee" strokeWidth={2} fillOpacity={1} fill="url(#colorLicense)" name="license_revenue" />
              <Area type="monotone" dataKey="work_order_revenue" stroke="#34d399" strokeWidth={2} fillOpacity={1} fill="url(#colorWorkOrder)" name="work_order_revenue" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
